import { useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Search,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SafeImage } from '@/components/ui/safe-image';
import type {
  CampaignPlacement,
  StoreProfileDetail,
} from '../../types';
import type { SpatialEntity } from '../../lib/spatialEntity';
import {
  CAMPAIGN_PAGE_SIZE,
  countCampaignsByPhase,
  enrichCampaign,
  filterCampaignsForBrowse,
  sortCampaignsForDisplay,
  type CampaignBrowseFilter,
} from '../../lib/campaignBrowseUtils';

interface CampaignsIssuesViewProps {
  profile: StoreProfileDetail;
  asOf: Date | null;
  onFocusLocation?: (selection: {
    kind: SpatialEntity['kind'];
    id: string;
  }) => void;
  onFocusCampaign?: (campaign: CampaignPlacement) => void;
}

const FILTER_OPTIONS: Array<{
  value: CampaignBrowseFilter;
  label: string;
}> = [
  { value: 'recent', label: 'Recent' },
  { value: 'active', label: 'Active' },
  { value: 'previous', label: 'Previous' },
  { value: 'planned', label: 'Planned' },
  { value: 'all', label: 'All' },
];

function formatDate(value: string | null | undefined): string | null {
  if (!value || value === '—') return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-IE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatCampaignDate(campaign: CampaignPlacement): string | null {
  const start = formatDate(campaign.installationDate);
  const end = formatDate(campaign.expectedRemovalDate);

  if (start && end) {
    const shortStart = new Date(campaign.installationDate).toLocaleDateString(
      'en-IE',
      {
        day: 'numeric',
        month: 'short',
      },
    );
    return `${shortStart} – ${end}`;
  }
  if (start || end) return start || end;

  const evidenceDate = formatDate(campaign.latestImageDate);
  return evidenceDate ? `Evidence captured ${evidenceDate}` : null;
}

function phaseLabel(phase: CampaignPlacement['phase']): string {
  if (phase === 'current') return 'Active';
  if (phase === 'planned') return 'Planned';
  return 'Previous';
}

function phaseBadgeClass(phase: CampaignPlacement['phase']): string {
  if (phase === 'current') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (phase === 'planned') {
    return 'border-blue-200 bg-blue-50 text-blue-700';
  }
  return 'border-slate-200 bg-white/90 text-slate-600';
}

function CampaignCard({
  campaign,
  expanded,
  onToggle,
  onShowOnMap,
  profile,
}: {
  campaign: CampaignPlacement;
  expanded: boolean;
  onToggle: () => void;
  onShowOnMap?: () => void;
  profile: StoreProfileDetail;
}) {
  const enriched = enrichCampaign(campaign, profile);
  const heroImage = enriched.primaryImageUrl;
  const campaignDate = formatCampaignDate(campaign);
  const showAdvertiser =
    campaign.advertiser &&
    campaign.advertiser.trim().toLocaleLowerCase() !==
      campaign.campaignName.trim().toLocaleLowerCase();

  return (
    <Card className="group overflow-hidden border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
      <button
        type="button"
        className="block w-full text-left"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <div className="relative aspect-[16/9] overflow-hidden bg-slate-100">
          {heroImage ? (
            <SafeImage
              src={heroImage}
              alt=""
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              No evidence image
            </div>
          )}
          <Badge
            variant="outline"
            className={`absolute right-3 top-3 shadow-sm ${phaseBadgeClass(
              enriched.phase,
            )}`}
          >
            {phaseLabel(enriched.phase)}
          </Badge>
        </div>

        <div className="space-y-3 p-4">
          <div>
            <h3 className="line-clamp-2 font-semibold leading-snug text-slate-950">
              {campaign.campaignName}
            </h3>
            {showAdvertiser ? (
              <p className="mt-1 text-sm text-slate-500">
                {campaign.advertiser}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5 text-sm text-slate-600">
            {campaignDate ? <p>{campaignDate}</p> : null}
            {campaign.instoreLocationName ? <p>{campaign.instoreLocationName}</p> : null}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {(campaign.mediaFormats?.length
                ? campaign.mediaFormats
                : [{ mediaFormatId: campaign.mediaFormatId ?? null, name: campaign.formatLabel, quantity: campaign.plannedQuantity }]
              ).map((format) => (
                <span
                  key={`${format.mediaFormatId ?? format.name}`}
                  className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600"
                >
                  {format.name} ×{format.quantity}
                </span>
              ))}
            </div>
          </div>

          <div className="flex min-h-5 items-center justify-between gap-3">
            {enriched.onMap ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                <MapPin className="h-3.5 w-3.5" />
                Viewable on map
              </span>
            ) : (
              <span />
            )}
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-slate-400" />
            )}
          </div>
        </div>
      </button>

      {expanded ? (
        <div className="border-t border-slate-100 bg-slate-50/70 p-4">
          {enriched.evidenceImages.length > 0 ? (
            <div className="mb-4 grid grid-cols-3 gap-2">
              {enriched.evidenceImages.slice(0, 6).map((image) => (
                <SafeImage
                  key={image.id}
                  src={image.url}
                  alt=""
                  className="aspect-square w-full rounded-md border border-slate-200 object-cover"
                />
              ))}
            </div>
          ) : (
            <p className="mb-4 text-sm text-slate-500">
              No additional evidence has been captured.
            </p>
          )}

          {enriched.onMap && onShowOnMap ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              onClick={onShowOnMap}
            >
              <MapPin className="mr-2 h-4 w-4" />
              Show on store map
            </Button>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

export function CampaignsIssuesView({
  profile,
  asOf,
  onFocusLocation,
  onFocusCampaign,
}: CampaignsIssuesViewProps) {
  const [filter, setFilter] = useState<CampaignBrowseFilter>('recent');
  const [search, setSearch] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [expandedCampaignId, setExpandedCampaignId] = useState<string | null>(
    null,
  );
  const referenceDate = asOf ?? new Date();

  const phaseCounts = useMemo(
    () => countCampaignsByPhase(profile.campaigns, referenceDate),
    [profile.campaigns, referenceDate],
  );
  const allBrowsableCampaigns = useMemo(
    () =>
      sortCampaignsForDisplay(
        filterCampaignsForBrowse(profile.campaigns, 'all', referenceDate),
      ),
    [profile.campaigns, referenceDate],
  );
  const locationOptions = useMemo(
    () =>
      Array.from(
        new Set(
          allBrowsableCampaigns
            .map((campaign) => campaign.instoreLocationName?.trim())
            .filter((location): location is string => Boolean(location)),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [allBrowsableCampaigns],
  );
  const filteredCampaigns = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();

    return sortCampaignsForDisplay(
      filterCampaignsForBrowse(profile.campaigns, filter, referenceDate),
    ).filter((campaign) => {
      if (
        locationFilter !== 'all' &&
        campaign.instoreLocationName !== locationFilter
      ) {
        return false;
      }

      if (!query) return true;

      return [
        campaign.campaignName,
        campaign.advertiser,
        campaign.instoreLocationName,
        campaign.formatLabel,
        campaign.format,
        ...(campaign.mediaFormats?.map((format) => format.name) ?? []),
      ].some((value) => value?.toLocaleLowerCase().includes(query));
    });
  }, [filter, locationFilter, profile.campaigns, referenceDate, search]);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredCampaigns.length / CAMPAIGN_PAGE_SIZE),
  );
  const safePage = Math.min(page, pageCount - 1);
  const paginatedCampaigns = filteredCampaigns.slice(
    safePage * CAMPAIGN_PAGE_SIZE,
    (safePage + 1) * CAMPAIGN_PAGE_SIZE,
  );
  const updateFilter = (nextFilter: CampaignBrowseFilter) => {
    setFilter(nextFilter);
    setPage(0);
    setExpandedCampaignId(null);
  };

  const handleShowOnMap = (campaign: CampaignPlacement) => {
    const mapFocus = enrichCampaign(campaign, profile).mapFocus;

    if (mapFocus && onFocusLocation) {
      onFocusLocation(mapFocus);
      return;
    }

    onFocusCampaign?.(campaign);
  };

  return (
    <div className="space-y-8">
      <section className="space-y-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Store story
            </p>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">
              Campaign activity
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              Browse the installations and evidence captured in this store.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span
              className={`rounded-full px-3 py-1.5 font-medium ${
                phaseCounts.current > 0
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-slate-100 text-slate-600'
              }`}
            >
              {phaseCounts.current > 0
                ? `${phaseCounts.current} active today`
                : 'No campaigns live today · showing recent activity'}
            </span>
          </div>
        </div>

        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <div className="flex flex-wrap gap-1">
            {FILTER_OPTIONS.map((option) => (
              <Button
                key={option.value}
                type="button"
                size="sm"
                variant={filter === option.value ? 'default' : 'ghost'}
                className="shrink-0"
                onClick={() => updateFilter(option.value)}
              >
                {option.label}
                {option.value !== 'recent' && option.value !== 'all' ? (
                  <span className="ml-1.5 opacity-70">
                    {phaseCounts[option.value === 'active' ? 'current' : option.value]}
                  </span>
                ) : null}
              </Button>
            ))}
          </div>

          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_240px]">
            <label className="relative">
              <span className="sr-only">Search campaign activity</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                className="pl-9"
                placeholder="Search campaign, advertiser or format"
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
              />
            </label>

            <label>
              <span className="sr-only">Filter by store location</span>
              <select
                value={locationFilter}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                onChange={(event) => {
                  setLocationFilter(event.target.value);
                  setPage(0);
                }}
              >
                <option value="all">All store locations</option>
                {locationOptions.map((location) => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            {filteredCampaigns.length}{' '}
            {filteredCampaigns.length === 1 ? 'campaign' : 'campaigns'}
          </p>
          {pageCount > 1 ? (
            <p className="text-xs text-slate-400">
              Page {safePage + 1} of {pageCount}
            </p>
          ) : null}
        </div>

        {paginatedCampaigns.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {paginatedCampaigns.map((campaign) => (
              <CampaignCard
                key={campaign.id}
                campaign={campaign}
                profile={profile}
                expanded={expandedCampaignId === campaign.id}
                onToggle={() =>
                  setExpandedCampaignId((current) =>
                    current === campaign.id ? null : campaign.id,
                  )
                }
                onShowOnMap={
                  enrichCampaign(campaign, profile).onMap
                    ? () => handleShowOnMap(campaign)
                    : undefined
                }
              />
            ))}
          </div>
        ) : (
          <Card className="border-dashed border-slate-200 bg-slate-50/60 shadow-none">
            <CardContent className="flex min-h-40 flex-col items-center justify-center p-6 text-center">
              <p className="font-medium text-slate-800">
                No campaigns match this view
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Try another status, store location or search term.
              </p>
            </CardContent>
          </Card>
        )}

        {pageCount > 1 ? (
          <div className="flex items-center justify-center gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={safePage === 0}
              onClick={() => {
                setPage((current) => Math.max(0, current - 1));
                setExpandedCampaignId(null);
              }}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={safePage >= pageCount - 1}
              onClick={() => {
                setPage((current) => Math.min(pageCount - 1, current + 1));
                setExpandedCampaignId(null);
              }}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        ) : null}
      </section>

    </div>
  );
}
